import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { FileText, Download, Calendar, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NumerosPage() {
  const [issues, setIssues] = useState([]);
  const [byYear, setByYear] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedYears, setExpandedYears] = useState({});

  useEffect(() => {
    fetchIssues();
  }, []);

  const fetchIssues = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pdf-issues`);
      const data = await response.json();
      setIssues(data.issues || []);
      setByYear(data.by_year || {});
      
      // Expand recent years by default
      const years = Object.keys(data.by_year || {}).sort().reverse();
      const expanded = {};
      years.slice(0, 3).forEach(y => expanded[y] = true);
      setExpandedYears(expanded);
    } catch (error) {
      console.error('Error fetching issues:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleYear = (year) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }));
  };

  const sortedYears = Object.keys(byYear).sort().reverse();

  return (
    <div className="min-h-screen bg-stone-50">
      <SEOHead 
        title="Tous les numéros PDF | VersaillesPlus Archives"
        description="Téléchargez tous les numéros de VersaillesPlus en PDF. Archives complètes depuis 2007."
      />
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero */}
        <div className="bg-gradient-to-br from-red-800 to-red-900 rounded-lg p-8 md:p-12 mb-12 text-white">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="h-8 w-8 text-red-300" />
            <h1 className="font-playfair text-3xl md:text-4xl font-bold">
              Tous les numéros
            </h1>
          </div>
          
          <p className="font-lora text-lg text-red-100 mb-6 max-w-3xl leading-relaxed">
            Retrouvez l'intégralité des numéros de VersaillesPlus en version PDF. 
            Depuis avril 2007, nous chroniquons la vie versaillaise avec passion et indépendance.
          </p>
          
          <div className="flex flex-wrap gap-4 text-sm font-inter">
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
              <FileText className="h-4 w-4" />
              <span>{issues.length} numéros (N°1 à N°157)</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
              <Calendar className="h-4 w-4" />
              <span>Avril 2007 - Juin 2023</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700 mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedYears.map((year) => (
              <div key={year} className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                <button
                  onClick={() => toggleYear(year)}
                  className="w-full flex items-center justify-between px-6 py-4 hover:bg-stone-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {expandedYears[year] ? (
                      <ChevronDown className="h-5 w-5 text-stone-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-stone-400" />
                    )}
                    <span className="font-playfair text-xl font-semibold text-stone-900">
                      {year}
                    </span>
                    <span className="bg-red-100 text-red-800 text-xs font-inter px-2 py-1 rounded-full">
                      {byYear[year]?.length || 0} numéros
                    </span>
                  </div>
                </button>
                
                {expandedYears[year] && (
                  <div className="border-t border-stone-100 px-6 py-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {byYear[year]?.map((issue, idx) => (
                        <Link
                          key={idx}
                          to={`/numero/${issue.num_value}`}
                          className="flex items-center gap-3 p-4 bg-stone-50 rounded-lg hover:bg-red-50 hover:border-red-200 border border-transparent transition-colors group"
                        >
                          <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200 transition-colors">
                            <FileText className="h-6 w-6 text-red-700" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-inter font-medium text-stone-900 truncate">
                              N°{issue.num_value}
                            </p>
                            <p className="font-inter text-sm text-stone-500">
                              {issue.mois && issue.annee ? `${issue.mois} ${issue.annee}` : issue.annee || ''}
                            </p>
                          </div>
                          {issue.pdf_url_local ? (
                            <Download className="h-5 w-5 text-green-600 flex-shrink-0" title="PDF disponible localement" />
                          ) : (
                            <ExternalLink className="h-5 w-5 text-stone-400 group-hover:text-red-600 flex-shrink-0" />
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Info box */}
        <div className="mt-12 bg-amber-50 border border-amber-200 rounded-lg p-6">
          <h3 className="font-playfair text-lg font-semibold text-amber-900 mb-2">
            📚 Vous cherchez un article précis ?
          </h3>
          <p className="font-inter text-sm text-amber-800 mb-4">
            Les articles de ces numéros sont progressivement numérisés et indexés. 
            Consultez nos archives en ligne pour rechercher par thème ou par date.
          </p>
          <Link 
            to="/archives"
            className="inline-flex items-center gap-2 text-red-700 font-inter text-sm font-medium hover:text-red-800"
          >
            Voir les articles archivés
            <ExternalLink className="h-4 w-4" />
          </Link>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
